// What the Drift page shares between its two views (by machine, by application): the kinds of finding with their words, colours and
// order, and the small helpers that read a finding.

export const list = (v) => (Array.isArray(v) ? v : []);

// What each kind of finding is called, how it is coloured and where it sorts. The words are the ones a customer reads.
export const GAPS = {
  identityMismatch: { label: 'Wrong machine id', tone: 'err', rank: 0, meaning: 'The machine id belongs to a different computer, so the profiles shown may be wrong.' },
  'forbidden-present': { label: 'Forbidden', tone: 'err', rank: 1, meaning: 'The policy says this software must not be installed.' },
  missing: { label: 'Missing', tone: 'err', rank: 2, meaning: 'The policy requires this software and it is not installed.' },
  outdated: { label: 'Outdated', tone: 'warn', rank: 3, meaning: 'A newer version is available.' },
  oldcopy: { label: 'Old copy', tone: 'warn', rank: 3.2, meaning: 'A newer copy of this program is also installed. Updating cannot remove this one; it has to be removed.' },
  conflict: { label: 'Profiles disagree', tone: 'warn', rank: 5, meaning: 'Two profiles at the same level disagree; forbidden won.' },
  unverified: { label: 'Unverified', tone: 'none', rank: 4, meaning: 'Installed, but there is no way to tell whether it is current.' },
  noDefinition: { label: 'Not set up', tone: 'none', rank: 6, meaning: 'The policy names software that has not been set up yet.' },
  invalidPolicy: { label: 'Bad rule', tone: 'none', rank: 7, meaning: 'A rule has an unknown setting.' },
};
export const gap = (g) => GAPS[g] || { label: g || '-', tone: 'none', rank: 99, meaning: '' };
export const MODE = { required: 'Required', allowed: 'Allowed', forbidden: 'Forbidden' };
export const NEEDS_SCRIPT = ['outdated', 'missing', 'forbidden-present', 'oldcopy'];

export const hasJob = (f) => Boolean(f?.checkJob && f?.applyJob);
export const versionText = (f) => {
  if (f?.installed && f?.available && f.installed !== f.available) return `${f.installed} → ${f.available}`;
  if (!f?.installed && f?.available) return `newest ${f.available}`;
  return f?.installed || '';
};

// "1.111.0 → 1.142.0": an installed version and the newest one, when the newest is higher (compared number by number). Otherwise just the
// installed version, so a program that is current, or newer than our copy of the catalog, never shows an arrow.
const numbersOf = (v) => (String(v || '').match(/\d+/g) || []).map(Number);
export const targetText = (installed, available) => {
  if (!installed || !available) return installed || '';
  const a = numbersOf(installed);
  const b = numbersOf(available);
  for (let i = 0; i < Math.max(a.length, b.length); i += 1) {
    const x = a[i] || 0;
    const y = b[i] || 0;
    if (x !== y) return x < y ? `${installed} → ${available}` : installed;
  }
  return installed;
};

// The name a finding is shown under: the catalog's name for the program, not the name the machine reports (those all differ).
export const shownName = (f) => f?.displayName || f?.name || f?.key || '';

// Where the machine lists the program: machine-wide, or for a user. It is where the entry is recorded, not who installed it: an installer can
// list a program machine-wide that belongs to one user, so the words say "listed".
export const scopeText = (scope) => {
  if (scope === 'machine') return 'Listed machine-wide';
  if (scope === 'user') return 'Listed for a user';
  if (scope === 'system-profile') return 'Listed in the system profile';
  return '';
};

// How the installed program was recognised: by the program id the machine reported for it, or by its name (no id, or a custom rule).
export const matchedText = (f) => {
  if (f?.matchedBy === 'id') return 'Matched by id';
  if (f?.matchedBy === 'name') return 'Matched by name';
  return '';
};
